"use client";

import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Footer } from "@/components/sections/premium/ManifestoSection";
import Link from "next/link";

const services = [
    {
        id: "ai",
        title: "AI Systems & Automation",
        description: "We build systems that actually do the work.",
        capabilities: ["AI agents", "automation pipelines", "real-world integrations", "LLM Orchestration", "Data processing"]
    },
    {
        id: "product",
        title: "Product Engineering",
        description: "We design and build production-ready products with scalable architecture.",
        capabilities: ["web apps", "system design", "performant backends", "cloud native", "API development"]
    },
    {
        id: "web3",
        title: "Web3 & Blockchain",
        description: "Securing the next generation of decentralized finance.",
        capabilities: ["smart contracts", "on-chain logic", "protocol design", "tokenomics", "DeFi infrastructure"]
    },
    {
        id: "design",
        title: "Design & User Experience",
        description: "High-fidelity interfaces that prioritize clarity and flow.",
        capabilities: ["UI/UX design", "prototypes", "interactive systems", "motion design", "design systems"]
    },
    {
        id: "branding",
        title: "Branding & Visual Identity",
        description: "Visual foundations for high-value products.",
        capabilities: ["brand strategy", "visual systems", "identity design", "typography", "art direction"]
    },
    {
        id: "motion",
        title: "Motion & Visual Content",
        description: "Cinematic storytelling through digital movement.",
        capabilities: ["3D visualization", "motion systems", "dynamic assets", "video production", "VFX"]
    },
    {
        id: "growth",
        title: "Growth & Infrastructure",
        description: "Infrastructure that scales as fast as the product.",
        capabilities: ["cloud architecture", "performance loops", "data systems", "DevOps", "analytics"]
    }
];

const ServiceRow = ({ service, index }: { service: any, index: number }) => {
    return (
        <div className="relative w-full min-h-[80vh] flex items-start py-32 border-b border-white/5 group">
            <div className="max-w-[1400px] w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 relative">
                
                {/* MASSIVE BACKGROUND ID */}
                <div className="absolute top-1/2 right-0 -translate-y-1/2 text-[30vw] font-display text-white/[0.02] leading-none pointer-events-none select-none text-right w-full z-0 group-hover:text-primary/[0.02] transition-colors duration-1000">
                    0{index + 1}
                </div>

                {/* LEFT COLUMN: STICKY TITLE */}
                <div className="lg:col-span-5 relative z-10 lg:sticky top-32 h-fit">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 rounded-full border border-primary flex justify-center items-center text-primary font-mono text-sm group-hover:bg-primary group-hover:text-black transition-all duration-500 scale-90 group-hover:scale-100">
                            0{index + 1}
                        </div>
                    </div>
                    <h2 className="font-display text-6xl md:text-8xl uppercase leading-[0.85] tracking-tight group-hover:text-primary transition-colors duration-500">
                        {service.title}
                    </h2>
                </div>

                {/* RIGHT COLUMN: SCROLLING CONTENT */}
                <div className="lg:col-span-7 flex flex-col justify-center relative z-10 lg:pl-16 pt-12 lg:pt-0">
                    <div className="space-y-16 max-w-2xl">
                        <p className="font-sans text-3xl md:text-5xl text-white/90 uppercase leading-snug tracking-tight font-light italic">
                            "{service.description}"
                        </p>
                        
                        <div className="pt-12 border-t border-white/10">
                            <span className="font-mono text-xs text-primary uppercase tracking-[0.3em] block mb-8">/ Core Capabilities</span>
                            <div className="flex flex-wrap gap-4">
                                {service.capabilities.map((cap: string, i: number) => (
                                    <motion.span
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-10%" }}
                                        transition={{ duration: 0.5, delay: i * 0.1 }}
                                        key={cap}
                                        className="font-sans text-lg md:text-xl uppercase tracking-tight text-white/70 hover:text-white border border-white/10 px-6 py-3 rounded-full hover:border-white/40 transition-colors cursor-default bg-white/[0.02] backdrop-blur-sm"
                                    >
                                        {cap}
                                    </motion.span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ServicesPageContent = () => {
    return (
        <main className="relative bg-[#0a0a0a] text-[#f4f4f0] min-h-screen selection:bg-primary selection:text-white font-sans overflow-hidden">
            {/* COLLINS STYLE HERO */}
            <section className="relative h-screen w-full flex flex-col justify-between p-6 md:p-12 overflow-hidden bg-black">
                <div className="absolute inset-0 z-0">
                    <video
                        src="/services.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale mix-blend-screen scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]" />
                </div>
                
                <div className="relative z-10 flex justify-between items-start mt-24">
                    <motion.span 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/50"
                    >
                        DOTSLASHLAB / EXPERTISE
                    </motion.span>
                    <motion.span 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="font-mono text-xs md:text-sm text-white/30 text-right tracking-widest uppercase"
                    >
                        SYSTEM_READY <br/> [ 01 ]
                    </motion.span>
                </div>

                <div className="relative z-10 w-full flex justify-center items-center h-full">
                    <motion.h1 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display text-[15vw] md:text-[18vw] leading-[0.75] tracking-tight uppercase text-center"
                    >
                        BUILT WITH <br />
                        <span className="text-transparent italic" style={{ WebkitTextStroke: '2px #f4f4f0' }}>INTENT.</span>
                    </motion.h1>
                </div>
            </section>

            {/* SERVICES LIST - STICKY SCROLL */}
            <section className="relative z-10 bg-[#0a0a0a]">
                {services.map((service, index) => (
                    <ServiceRow key={service.id} service={service} index={index} />
                ))}
            </section>

            {/* HIGH CONTRAST BLOCK IN SERVICES */}
            <section className="relative z-10 px-4 py-32">
                <div className="max-w-[1400px] mx-auto">
                    <div className="bg-[#f4f4f0] text-[#0a0a0a] p-12 md:p-32 rounded-[2rem] shadow-2xl flex flex-col items-center text-center relative overflow-hidden group">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#e0e0e0_100%)] opacity-50" />
                        <h2 className="font-display text-6xl md:text-[8vw] uppercase tracking-tight leading-[0.8] mb-12 relative z-10">
                            Ready to <br/> <span className="italic text-transparent" style={{ WebkitTextStroke: '2px #0a0a0a' }}>execute?</span>
                        </h2>
                        <Link 
                            href="/contact" 
                            className="relative z-10 inline-block border-2 border-black px-12 py-6 rounded-full font-sans text-2xl uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-500 hover:scale-105 active:scale-95"
                        >
                            Start a Project
                        </Link>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <Footer />

            {/* Background premium detail */}
            <div className="bg-noise opacity-10 pointer-events-none fixed inset-0 z-50 mix-blend-overlay" />
        </main>
    );
};
